import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    createStory,
    deleteStory,
    EPHEMERAL_STORY_KEY,
    EPHEMERAL_STORY_LIKERS_KEY,
    EPHEMERAL_STORY_RINGS_KEY,
    EPHEMERAL_STORY_RINGS_ALL_KEY,
    EPHEMERAL_STORY_RINGS_EXPLORER_KEY,
    EPHEMERAL_STORY_USER_RING_KEY,
    EPHEMERAL_STORY_AUDIENCE_KEY,
    EPHEMERAL_STORY_VIEWERS_KEY,
    fetchMyStoryAudience,
    fetchRings,
    fetchExplorerRings,
    fetchStory,
    fetchStoryLikers,
    fetchStoryViewers,
    fetchUserStoryRing,
    markStoryViewed,
    uploadStoryFile
} from "../api/public-voissa/ephemeralStories";
import { EPHEMERAL_STORY } from "../const/toasts";
import { USER_CURRENT_KEY } from "../api/public-voissa/users";
import { toastError, toastSuccess } from "../libs/toast";
import { STORY_QUOTA_KIND, withStoryQuota } from "../libs/storyQuota";

// staleTime: 0 sur rings → on refetch à chaque mount/focus pour récupérer dès que possible la
// version backend la plus à jour (hasUnseen, latestAt, ajouts/suppressions d'autres users).
// Le cache (cacheTime default 5min) reste actif pour éviter un flash entre deux mounts rapprochés.
const RINGS_STALE_TIME_MS = 0;
const STORY_STALE_TIME_MS = 30 * 1000;
const VIEWERS_PAGE_LIMIT = 20;

// L'API a un cache rings 30 s par viewer (cf. brief). Une invalidation immédiate après publication
// récupèrerait l'ancien cache → on écraserait l'optimistic update. On diffère donc l'invalidation
// au-delà de ce TTL pour resync avec la vraie donnée backend.
const BACKEND_RINGS_CACHE_TTL_MS = 31 * 1000;

// Extrait les champs d'une story pour l'optimistic update du ring strip après création.
// IMPORTANT : inclure les champs d'AFFICHAGE (previewUrl / videoUrlFormat / videoCoverUrl) —
// sans eux, ouvrir sa propre story juste publiée (avant le refetch backend) ouvre un viewer
// au média vide (page blanche), car StoryMedia lit ces champs directement.
const pickRingStoryFields = (story) => ({
    _id: story._id,
    kind: story.kind,
    title: story.title,
    right: story.right,
    fileKey: story.fileKey,
    fileUUID: story.fileUUID,
    state: story.state,
    expiresAt: story.expiresAt,
    createdAt: story.createdAt,
    previewUrl: story.previewUrl,
    videoCoverUrl: story.videoCoverUrl,
    videoUrl: story.videoUrl,
    videoUrlFormat: story.videoUrlFormat,
    seen: story.seen ?? true,            // ma propre story est toujours considérée "vue"
    userReaction: story.userReaction ?? null,
    ...(story.duration ? { duration: story.duration } : {})
});

// Liste des rings groupée par owner (UNSEEN first).
const useQueryStoryRings = ({ enabled = true } = {}) => {
    return useQuery(
        [EPHEMERAL_STORY_RINGS_KEY],
        () => fetchRings(),
        {
            enabled,
            staleTime: RINGS_STALE_TIME_MS,
            // Pas de refetch au focus : un ring VU ne doit pas disparaître en cours de visionnage. Le
            // strip se met à jour à la fermeture du viewer (?fresh=1) et au reload / retour sur la page
            // — suffisant pour des stories qui durent plusieurs heures.
            refetchOnWindowFocus: false,
            // Le backend retourne un array ; en cas d'erreur réseau, on garde [] pour ne pas casser le strip.
            select: (data) => Array.isArray(data) ? data : []
        }
    );
};

// Filtres Découvrir par défaut (objet stable → clé de cache stable quand aucun filtre actif).
const EMPTY_RINGS_FILTER = {};

// Rings "Découvrir" ADMIN : toutes les stories actives (scope=all). Réservé aux admins côté API
// (403 sinon) → n'activer que pour un admin. Clé de cache distincte de la liste "Suivis".
// `filter` = filtres Découvrir (gender/nsfw) ; il entre dans la queryKey (React Query hash par
// VALEUR → pas de refetch tant que les valeurs ne changent pas) et dans la querystring envoyée.
const useQueryStoryRingsAll = ({ enabled = true, filter = EMPTY_RINGS_FILTER } = {}) => {
    return useQuery(
        [EPHEMERAL_STORY_RINGS_ALL_KEY, filter],
        () => fetchRings({ scope: 'all', ...filter }),
        {
            enabled,
            staleTime: RINGS_STALE_TIME_MS,
            // Pas de refetch au focus (cf. useQueryStoryRings) : maj à la fermeture du viewer + reload.
            refetchOnWindowFocus: false,
            retry: false,
            select: (data) => Array.isArray(data) ? data : []
        }
    );
};

// Ring "Explorer" ADMIN (phase 1) : endpoint DÉDIÉ /explorer — stories "à la une" d'abord (ordre de
// modération) puis récence, périmètre membre. Réservé aux admins + canViewStories côté API (403 sinon)
// → n'activer qu'à ces conditions. Clé de cache distincte de "Suivis" et "Découvrir".
const useQueryStoryRingsExplorer = ({ enabled = true, filter = EMPTY_RINGS_FILTER } = {}) => {
    return useQuery(
        [EPHEMERAL_STORY_RINGS_EXPLORER_KEY, filter],
        () => fetchExplorerRings(filter),
        {
            enabled,
            staleTime: RINGS_STALE_TIME_MS,
            refetchOnWindowFocus: false,
            retry: false,
            select: (data) => Array.isArray(data) ? data : []
        }
    );
};

// Audience de mes stories (encarts d'incitation). Le serveur met déjà le calcul en cache 1 h ; on
// s'aligne côté client avec un staleTime long — un chiffre à 7 jours n'a pas besoin d'être frais
// à la seconde, et l'encart s'affiche sur la home.
const AUDIENCE_STALE_TIME_MS = 10 * 60 * 1000;

const useQueryMyStoryAudience = ({ enabled = true } = {}) => {
    return useQuery(
        [EPHEMERAL_STORY_AUDIENCE_KEY],
        () => fetchMyStoryAudience(),
        {
            enabled,
            staleTime: AUDIENCE_STALE_TIME_MS,
            retry: false,
            refetchOnWindowFocus: false
        }
    );
};

// Ring d'un user spécifique — utilisé par l'avatar de la page profil pour décider de l'effet visuel
// (anneau coloré / gris / rien) et de l'action au tap. Pas de retry, on tolère 404 (= pas de story).
const useQueryUserStoryRing = (userId, { enabled = true } = {}) => {
    return useQuery(
        [EPHEMERAL_STORY_USER_RING_KEY, userId],
        () => fetchUserStoryRing(userId),
        {
            enabled: enabled && !!userId,
            staleTime: RINGS_STALE_TIME_MS,
            retry: false,
            refetchOnWindowFocus: true
        }
    );
};

// Fetch d'une story unique (deep-link). On laisse l'erreur remonter pour afficher <StoryUnavailable />.
const useQueryStory = (storyId, { enabled = true } = {}) => {
    return useQuery(
        [EPHEMERAL_STORY_KEY, storyId],
        () => fetchStory(storyId),
        {
            enabled: enabled && !!storyId,
            staleTime: STORY_STALE_TIME_MS,
            // Pas de retry sur 404/403 (le retry global du QueryClient le fait déjà via statusCodeErrorNoRetry).
            retry: false
        }
    );
};

// Liste paginée des viewers d'une story (owner uniquement).
const useInfiniteStoryViewers = (storyId, { enabled = true, limit = VIEWERS_PAGE_LIMIT } = {}) => {
    return useInfiniteQuery(
        [EPHEMERAL_STORY_VIEWERS_KEY, storyId, limit],
        ({ pageParam = 1 }) => fetchStoryViewers(storyId, { page: pageParam, limit }),
        {
            enabled: enabled && !!storyId,
            getNextPageParam: (lastPage) => {
                if (!lastPage) return undefined;
                const total = lastPage.total || 0;
                const page = lastPage.page || 1;
                const totalPages = Math.ceil(total / (lastPage.limit || limit));
                return page < totalPages ? page + 1 : undefined;
            }
        }
    );
};

// Liste paginée des likers d'une story (owner/admin uniquement).
const useInfiniteStoryLikers = (storyId, { enabled = true, limit = VIEWERS_PAGE_LIMIT } = {}) => {
    return useInfiniteQuery(
        [EPHEMERAL_STORY_LIKERS_KEY, storyId, limit],
        ({ pageParam = 1 }) => fetchStoryLikers(storyId, { page: pageParam, limit }),
        {
            enabled: enabled && !!storyId,
            // Enveloppe de la liste de réactions : { data, more, total } (cf. AdvancedMediaViewerStats / LikeList).
            getNextPageParam: (lastPage, pages) => (lastPage && lastPage.more ? pages.length + 1 : undefined)
        }
    );
};

// Marquer une story comme vue (best-effort, on n'affiche rien en cas d'erreur).
// On NE touche PAS aux rings ici : la vue est comptée côté serveur, mais le retrait des rings
// entièrement vus est DIFFÉRÉ à la fermeture du viewer (cf. StoryViewerRoute) pour ne pas faire
// disparaître un ring en cours de lecture. La mise "hasUnseen=false" locale (sans refetch) est
// gérée par StoryViewer sur la dernière story du ring.
const useMutationMarkStoryViewed = () => {
    const queryClient = useQueryClient();
    return useMutation(
        (storyId) => markStoryViewed(storyId),
        {
            // L'API renvoie l'état du quota de lecture avec la vue. On patche le cache
            // currentUser plutôt que de l'invalider : le compteur descend sans requête
            // supplémentaire, et c'est ce qui refloute les vignettes dès la dernière story
            // autorisée. Un abonné n'est pas concerné (l'API renvoie access 'full').
            onSuccess: (res) => {
                const quota = res?.quota;
                if (!quota) return;
                queryClient.setQueryData([USER_CURRENT_KEY], (oldUser) =>
                    withStoryQuota(oldUser, STORY_QUOTA_KIND.VIEW, quota)
                );
            },
            // Silencieux : l'UX du viewer ne doit jamais être bloquée par cet appel.
            onError: () => {}
        }
    );
};

// Upload du fichier temporaire. Renvoie le tmpId à utiliser pour la création.
const useMutationUploadStoryFile = () => {
    return useMutation(({ file, onUploadProgress }) => uploadStoryFile(file, { onUploadProgress }));
};

// Création de la story à partir d'un tmpId.
const useMutationCreateStory = ({ onSuccess = () => {} } = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        (data) => createStory(data),
        {
            onSuccess: async (story) => {
                // === Optimistic update du ring strip ===
                // L'API a un cache rings 30s par viewer : on ne peut pas se fier à un re-fetch immédiat.
                // On insère donc directement la nouvelle story dans le cache local.
                if (story?._id && story?.owner?._id) {
                    queryClient.setQueryData([EPHEMERAL_STORY_RINGS_KEY], (oldRings) => {
                        const safeRings = Array.isArray(oldRings) ? oldRings : [];
                        const ownerId = story.owner._id;
                        const idx = safeRings.findIndex((r) => r?.owner?._id === ownerId);
                        const compactStory = pickRingStoryFields(story);

                        if (idx !== -1) {
                            // Ring existant pour cet owner : append la nouvelle story + remonte en tête.
                            const updatedRing = {
                                ...safeRings[idx],
                                hasUnseen: true,
                                latestAt: story.createdAt,
                                stories: [...(safeRings[idx].stories || []), compactStory]
                            };
                            const others = safeRings.filter((_, i) => i !== idx);
                            return [updatedRing, ...others];
                        }
                        // Pas de ring : on en crée un en tête (= Ma story).
                        return [{
                            owner: story.owner,
                            hasUnseen: true,
                            latestAt: story.createdAt,
                            stories: [compactStory]
                        }, ...safeRings];
                    });
                }

                // Pré-remplit le cache de la story elle-même (utile pour un deep-link immédiat).
                if (story?._id) {
                    queryClient.setQueryData([EPHEMERAL_STORY_KEY, story._id], story);
                }

                // === Optimistic update du ring "par owner" (anneau sur l'avatar de profil) ===
                // ProfileUserAvatar lit [EPHEMERAL_STORY_USER_RING_KEY, ownerId] ; sans mise à jour ici,
                // l'anneau n'apparaissait qu'après un reload de la page. Même cache backend que les rings
                // (30s), donc optimistic maintenant + invalidation différée pour resync.
                if (story?._id && story?.owner?._id) {
                    const compactStory = pickRingStoryFields(story);
                    queryClient.setQueryData([EPHEMERAL_STORY_USER_RING_KEY, story.owner._id], (oldRing) => {
                        if (oldRing && Array.isArray(oldRing.stories)) {
                            return { ...oldRing, latestAt: story.createdAt, stories: [...oldRing.stories, compactStory] };
                        }
                        // Aucun ring encore (réponse null/404) : on en crée un (mes stories = déjà "vues").
                        return { owner: story.owner, hasUnseen: false, latestAt: story.createdAt, stories: [compactStory] };
                    });
                }

                // Resync avec le backend APRÈS expiration de son cache (30s + marge).
                // Avant ce délai, un re-fetch ne ferait que ré-écraser notre optimistic avec l'ancien cache.
                setTimeout(() => {
                    queryClient.invalidateQueries([EPHEMERAL_STORY_RINGS_KEY]);
                    if (story?.owner?._id) {
                        queryClient.invalidateQueries([EPHEMERAL_STORY_USER_RING_KEY, story.owner._id]);
                    }
                }, BACKEND_RINGS_CACHE_TTL_MS);

                toastSuccess(EPHEMERAL_STORY.SUCCESS.CREATE);
                onSuccess(story);
            },
            // Pas de onError global : le caller (StoryCreatorPanel) affiche déjà l'erreur en INLINE
            // dans la modale via setPublishError → TextError. Un toast en plus serait redondant.
        }
    );
};

// Suppression d'une story (owner ou modérateur).
const useMutationDeleteStory = ({ onSuccess = () => {} } = {}) => {
    const queryClient = useQueryClient();
    return useMutation(
        (storyId) => deleteStory(storyId),
        {
            onSuccess: async (_, storyId) => {
                // === Optimistic update du ring strip ===
                // Retire la story du ring concerné, et supprime le ring entier s'il devient vide.
                queryClient.setQueryData([EPHEMERAL_STORY_RINGS_KEY], (oldRings) => {
                    if (!Array.isArray(oldRings)) return oldRings;
                    return oldRings
                        .map((ring) => {
                            if (!ring?.stories?.length) return ring;
                            const filtered = ring.stories.filter((s) => s?._id !== storyId);
                            if (filtered.length === ring.stories.length) return ring; // pas concerné
                            return { ...ring, stories: filtered };
                        })
                        .filter((ring) => ring?.stories?.length > 0);
                });

                // Purge des caches dépendants (story et viewers).
                queryClient.removeQueries([EPHEMERAL_STORY_KEY, storyId]);
                queryClient.removeQueries([EPHEMERAL_STORY_VIEWERS_KEY, storyId]);

                // Idem pour le ring "par owner" (avatar de profil) : on ne connaît pas l'owner ici,
                // on retire donc la story de tous les rings user en cache (et on nulle le ring vidé).
                queryClient.setQueriesData([EPHEMERAL_STORY_USER_RING_KEY], (oldRing) => {
                    if (!oldRing || !Array.isArray(oldRing.stories)) return oldRing;
                    const filtered = oldRing.stories.filter((s) => s?._id !== storyId);
                    if (filtered.length === oldRing.stories.length) return oldRing; // pas concerné
                    return filtered.length ? { ...oldRing, stories: filtered } : null;
                });

                // Resync différé (le cache backend rings peut encore renvoyer l'ancienne liste pendant 30s).
                setTimeout(() => {
                    queryClient.invalidateQueries([EPHEMERAL_STORY_RINGS_KEY]);
                    queryClient.invalidateQueries([EPHEMERAL_STORY_USER_RING_KEY]);
                }, BACKEND_RINGS_CACHE_TTL_MS);

                toastSuccess(EPHEMERAL_STORY.SUCCESS.DELETE);
                onSuccess(storyId);
            },
            onError: () => toastError(EPHEMERAL_STORY.ERROR.DELETE)
        }
    );
};

export {
    useQueryStoryRings,
    useQueryStoryRingsAll,
    useQueryStoryRingsExplorer,
    useQueryUserStoryRing,
    useQueryMyStoryAudience,
    useQueryStory,
    useInfiniteStoryViewers,
    useInfiniteStoryLikers,
    useMutationMarkStoryViewed,
    useMutationUploadStoryFile,
    useMutationCreateStory,
    useMutationDeleteStory
};
